"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { cancellationFormSchema } from "@/lib/utils";
import MyCustomFormField, { FormFieldType } from "./MyCustomFormField";
import { Loader2 } from "lucide-react";
import { SelectItem } from "./ui/select";
import { updateTripDetail } from "@/lib/actions/trip.actions";
import { toast } from "sonner";
import RatingReview from "./RatingReview";

const CANCELLATIONS_REASONS = [
  { value: "0", label: "Je n'ai plus besoin de chauffeur" },
  { value: "25", label: "Arrivée en retard du chauffeur" },
  { value: "24", label: "Message non lu par le chauffeur" },
  { value: "21", label: "Refus de course par le chauffeur" },
  { value: "20", label: "Chauffeur injoignable par téléphone" },
  { value: "27", label: "Non présentation du chauffeur" },
  {
    value: "28",
    label: "Erreur de tarification (7e forfait course immédiate)",
  },
  { value: "26", label: "Chauffeur non professionnel" },
  { value: "23", label: "Autre" },
];


const CancelPopover = ({
  course_token,
  box_with_cancel,
  box_with_rate,
  rating,
}: {
  course_token: string;
  box_with_cancel: boolean;
  box_with_rate: boolean;
  rating:number;
}) => {

  const formSchema = cancellationFormSchema();
  const [isLoading, setIsLoading] = useState(false);
  const [localRating, setLocalRating] = useState(rating);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const router = useRouter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      comment: "",
      reason: "0",
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    const updatedData = {rating:localRating, ...data};

    try {
      const action = box_with_cancel ? "cancel_taxi" : box_with_rate ? "rate_trip" : "comment_trip";
      const {data, error} = await updateTripDetail({ action, course_token, data:updatedData });
      if (error){
        toast.error(error);
      } else if (!data) {
        toast.success(`Une erreur s'est produite`);
      }else if (box_with_cancel) {
        toast.success(`Commande annulée`);
      }else if (box_with_rate){
        toast.success("Votre avis a bien été pris enregistré");
      } else{
        toast.success("Votre commentaire a bien été pris enregistré");
      }
    } catch (error) {
      console.error("onClickPreallocatedCancel", error);
    } finally {
      setCalendarOpen(false);
      setIsLoading(false);
      router.refresh();
    }
  };

  return (
    <div className="flex flex-col items-center"> 
      {(rating && rating > 0) && <RatingReview rating={rating} fontSize={12} setRating={()=>{}}/> }

      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className={(box_with_cancel ? "view-red-btn" : "view-all-btn")}>
            {box_with_cancel ?
              "Annuler":
              box_with_rate ? 
              "Commentaire" :
              "Commentaire"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 bg-white">
          <div className="grid gap-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none">{box_with_cancel ?
              "Annuler":
              box_with_rate ? 
              "Commentaire" :
              "Commentaire"}</h4>
            </div>
            <div>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  {(box_with_cancel || box_with_rate)  &&
                  <RatingReview rating={localRating} setRating={setLocalRating} />}
                  

                  {(box_with_cancel || box_with_rate)  &&
                    <MyCustomFormField
                      fieldType={FormFieldType.SELECT}
                      control={form.control}
                      name="name"
                      label=""
                      placeholder={box_with_cancel ? "Motif d'annulation ?" : "Incident ?"}
                    >
                      {CANCELLATIONS_REASONS.map(({ value, label }) => (
                        <div key={value}>
                          <SelectItem value={value}>{label}</SelectItem>
                        </div>
                      ))}
                    </MyCustomFormField>}

                    <MyCustomFormField
                      fieldType={FormFieldType.TEXTAREA}
                      control={form.control}
                      name="comment"
                      label= ""
                      placeholder={(box_with_cancel || box_with_rate) ? "Commentaire (facultatif)" : "Ce commentaire n'est pas envoyé au chauffeur"}
                      maxLength={20}
                    ></MyCustomFormField>

                  
                  <div className="flex flex-col gap-4">
                    <Button
                      type="submit"
                      className="form-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex">
                          <Loader2 size={20} className="animate-spin" /> &nbsp;
                          En cours...
                        </div>
                      ) : (
                        "Confirmer"
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CancelPopover;
